import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowUpRightIcon, SaveIcon, XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import z from "zod";

import { JobDifficultyUrgencyHistory } from "./JobDifficultyUrgencyHistory";
import { JobUpdateForm } from "./JobUpdateForm";
import { JobUpdateHistory } from "./JobUpdateHistory";
import { JobUpdateModalHeader } from "./JobUpdateModalHeader";
import {
  Column,
  ErrorText,
  KeyboardShortcut,
  LoadingIndicator,
  Row,
} from "@/components";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useCreateJobUpdate } from "@/hooks/mutations/useCreateJobUpdate";
import { useAddJobUpdate } from "@/hooks/sets/useAddJobUpdate";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  JobBottleneck,
  JobHealth,
  JobQueryParams,
  JobTrackerResponse,
} from "@/types";

const UPDATE_JOB_SCHEMA = z.object({
  description: z.string().min(1, "Description is required"),
  health: z.nativeEnum(JobHealth, {
    required_error: "A health selection is required",
  }),
  bottleneck: z.nativeEnum(JobBottleneck, {
    required_error: "A bottleneck is required",
  }),
});

export type UpdateJobFormValues = z.infer<typeof UPDATE_JOB_SCHEMA>;

// When no prior job update exists, default to PENDING health and CUSTOMER bottleneck.
// These serve as the initial form state before the useEffect resets to the latest update.
const DEFAULT_JOB_UPDATE_FORM_VALUES: UpdateJobFormValues = {
  description: "",
  health: JobHealth.PENDING,
  bottleneck: JobBottleneck.CUSTOMER,
};

const MODAL_BUTTON_CLASS_NAME =
  "relative h-8 p-2 focus:border-2 focus:border-primary-400";

export const JobUpdateModal = ({
  isJobUpdateModalOpen,
  jobQueryParams,
  jobs,
  selectedIndex,
  setIsJobUpdateModalOpen,
  setJobsForUpdateModal,
  setSelectedIndex,
}: {
  isJobUpdateModalOpen: boolean;
  jobQueryParams: JobQueryParams;
  jobs: JobTrackerResponse[];
  selectedIndex: number | null;
  setIsJobUpdateModalOpen: (isOpen: boolean) => void;
  setJobsForUpdateModal: Dispatch<SetStateAction<JobTrackerResponse[]>>;
  setSelectedIndex: Dispatch<SetStateAction<number | null>>;
}) => {
  /* Database CRUD Operations */
  // Create
  const {
    mutate: createJobUpdate,
    isPending: isCreateJobUpdatePending,
    isError: isCreateJobUpdateError,
  } = useCreateJobUpdate();

  /* Cache Updates */
  // Add
  const addJobUpdate = useAddJobUpdate();

  /* React Hooks */
  // useState
  const [selectedJob, setSelectedJob] = useState<JobTrackerResponse | null>(
    null,
  );

  const form = useForm<UpdateJobFormValues>({
    resolver: zodResolver(UPDATE_JOB_SCHEMA),
    defaultValues: DEFAULT_JOB_UPDATE_FORM_VALUES,
  });

  const isLastJob = selectedIndex === jobs.length - 1;

  // useCallback
  const onClose = useCallback(() => {
    setIsJobUpdateModalOpen(false);
    setSelectedIndex(null);
  }, [setIsJobUpdateModalOpen, setSelectedIndex]);

  const onPrevious = useCallback(() => {
    setSelectedIndex((idx) => Math.max(0, (idx ?? 0) - 1));
  }, [setSelectedIndex]);

  const onNext = useCallback(() => {
    setSelectedIndex((idx: number | null) => {
      const next = (idx ?? 0) + 1;
      if (next >= jobs.length) {
        setIsJobUpdateModalOpen(false);
        return idx ?? 0;
      }
      return next;
    });
  }, [jobs.length, setIsJobUpdateModalOpen, setSelectedIndex]);

  const saveJobUpdate = useCallback(
    (jobUpdateData: UpdateJobFormValues, onComplete: () => void) => {
      if (!selectedJob) return;
      mixpanelUtils.trackFormSubmission("Job update submit", MIXPANEL_SCREENS.JOB_TRACKER, {
        health: jobUpdateData.health,
        bottleneck: jobUpdateData.bottleneck,
      });
      createJobUpdate(
        {
          companyId: selectedJob.company.id,
          createJobUpdateRequest: jobUpdateData,
          jobId: selectedJob.jobId,
        },
        {
          onSuccess: (newJobUpdate) => {
            addJobUpdate({
              jobId: selectedJob.jobId,
              jobQueryParams,
              newJobUpdate,
            });
            setJobsForUpdateModal((currJobsInModal) => {
              return currJobsInModal.map((jobInModal) => {
                if (jobInModal.jobId === selectedJob.jobId) {
                  return {
                    ...jobInModal,
                    jobUpdates: [newJobUpdate, ...jobInModal.jobUpdates],
                  };
                }
                return jobInModal;
              });
            });
            onComplete();
          },
        },
      );
    },
    [
      createJobUpdate,
      addJobUpdate,
      selectedJob,
      jobQueryParams,
      setJobsForUpdateModal,
    ],
  );

  const handleSaveAndClose = useCallback(
    (data: UpdateJobFormValues) => {
      saveJobUpdate(data, onClose);
    },
    [saveJobUpdate, onClose],
  );

  const handleSaveAndNext = useCallback(
    (data: UpdateJobFormValues) => {
      saveJobUpdate(data, onNext);
    },
    [saveJobUpdate, onNext],
  );

  // useEffect
  // Default the form values to the latest job update
  useEffect(() => {
    if (!isJobUpdateModalOpen) return;
    const latestJobUpdate =
      selectedJob && selectedJob.jobUpdates.length > 0
        ? selectedJob.jobUpdates[0]
        : null;

    form.reset(
      latestJobUpdate
        ? {
            description: latestJobUpdate.description ?? "",
            health: latestJobUpdate.health ?? JobHealth.PENDING,
            bottleneck: latestJobUpdate.bottleneck ?? JobBottleneck.CUSTOMER,
          }
        : DEFAULT_JOB_UPDATE_FORM_VALUES,
    );
  }, [form, isJobUpdateModalOpen, jobs, selectedIndex, selectedJob]);

  // Focus description when modal opens and when flipping between jobs
  useEffect(() => {
    if (!isJobUpdateModalOpen) return;
    const timeoutId = window.setTimeout(() => {
      form.setFocus("description");
    });
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [form, isJobUpdateModalOpen, selectedIndex]);

  // Keyboard shortcuts for navigating between jobs and saving
  useEffect(() => {
    if (!isJobUpdateModalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey && e.shiftKey && e.key === "," && selectedIndex !== 0) {
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Cmd+Shift+< (Previous)",
          MIXPANEL_SCREENS.JOB_TRACKER,
        );
        onPrevious();
      } else if (e.metaKey && e.shiftKey && e.key === "." && !isLastJob) {
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Cmd+Shift+> (Next)",
          MIXPANEL_SCREENS.JOB_TRACKER,
        );
        onNext();
      } else if (e.key === "Enter" && e.metaKey) {
        e.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed(
          isLastJob
            ? "Cmd+Enter (Save & Close)"
            : "Cmd+Enter (Save & Next)",
          MIXPANEL_SCREENS.JOB_TRACKER,
        );
        void form.handleSubmit(
          isLastJob ? handleSaveAndClose : handleSaveAndNext,
        )();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [
    form,
    handleSaveAndClose,
    handleSaveAndNext,
    isLastJob,
    isJobUpdateModalOpen,
    onNext,
    onPrevious,
    selectedIndex,
  ]);

  // Set the selected job when the selected index changes
  useEffect(() => {
    if (
      selectedIndex === null ||
      selectedIndex < 0 ||
      selectedIndex >= jobs.length
    ) {
      setSelectedJob(null);
      return;
    }
    setSelectedJob(jobs[selectedIndex]);
  }, [form, jobs, selectedIndex]);

  const render = () => {
    let saveButtonTextClassName = "";
    let loadingIndicator = null;

    if (isCreateJobUpdatePending) {
      saveButtonTextClassName = "invisible";
      loadingIndicator = (
        <LoadingIndicator
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          variant="small"
        />
      );
    }

    let errorMessage = null;
    if (isCreateJobUpdateError) {
      errorMessage = (
        <Row className="justify-end px-5 pb-3">
          <ErrorText errorMessage="An error occurred while saving the job update. Please try again." />
        </Row>
      );
    }

    return (
      <Dialog
        open={isJobUpdateModalOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-5xl p-0" hideCloseButton>
          <Column className="gap-4 p-5 pb-1">
            <JobUpdateModalHeader
              isLastJob={isLastJob}
              jobCount={jobs.length}
              onNext={onNext}
              onPrevious={onPrevious}
              selectedIndex={selectedIndex}
              selectedJob={selectedJob}
            />

            <Tabs
              defaultValue="update"
              onValueChange={(value) => {
                mixpanelUtils.trackTabView(
                  MIXPANEL_SCREENS.JOB_TRACKER,
                  value,
                );
              }}
            >
              <TabsList className="gap-2">
                <TabsTrigger className="px-4 py-1.5 text-sm" value="update">
                  Update
                </TabsTrigger>
                <TabsTrigger className="px-4 py-1.5 text-sm" value="history">
                  History
                </TabsTrigger>
                <TabsTrigger
                  className="px-4 py-1.5 text-sm"
                  value="difficulty-urgency"
                >
                  Difficulty & Urgency
                </TabsTrigger>
              </TabsList>
              <TabsContent className="h-[300px] overflow-auto" value="update">
                <JobUpdateForm form={form} />
              </TabsContent>
              <TabsContent
                className="h-[300px] overflow-auto"
                value="history"
              >
                <JobUpdateHistory
                  jobUpdates={selectedJob?.jobUpdates ?? []}
                />
              </TabsContent>
              <TabsContent
                className="h-[300px] overflow-auto"
                value="difficulty-urgency"
              >
                <Column className="mt-2 gap-4">
                  {selectedJob && (
                    <Button
                      className="h-auto self-start p-0 text-primary-900"
                      onClick={() => {
                        mixpanelUtils.trackButtonClick(
                          "Update Difficulty & Urgency link",
                          MIXPANEL_SCREENS.JOB_TRACKER,
                        );
                        window.open(
                          `/companies/${selectedJob.company.id}/jobs/${selectedJob.jobId}?t=internal-config`,
                          "_blank",
                        );
                      }}
                      variant="link"
                    >
                      <ArrowUpRightIcon className="h-3.5 w-3.5" />
                      Update Difficulty & Urgency
                    </Button>
                  )}
                  <JobDifficultyUrgencyHistory
                    jobUpdates={selectedJob?.jobUpdates ?? []}
                  />
                </Column>
              </TabsContent>
            </Tabs>
          </Column>
          <DialogFooter className="border-t border-slate-200">
            <Column className="w-full gap-2">
              <Row className="w-full items-center justify-between px-5 py-3">
                <Button
                  className={MODAL_BUTTON_CLASS_NAME}
                  disabled={isCreateJobUpdatePending}
                  onClick={() => {
                    mixpanelUtils.trackButtonClick(
                      "Cancel Update",
                      MIXPANEL_SCREENS.JOB_TRACKER,
                    );
                    onClose();
                  }}
                  variant="outlineSecondary"
                >
                  <XIcon className="h-3 w-3" />
                  Cancel
                </Button>
                <Row className="items-center gap-2">
                  <Button
                    className={MODAL_BUTTON_CLASS_NAME}
                    disabled={isCreateJobUpdatePending}
                    onClick={() => {
                      mixpanelUtils.trackButtonClick(
                        "Save & Close",
                        MIXPANEL_SCREENS.JOB_TRACKER,
                      );
                      void form.handleSubmit(handleSaveAndClose)();
                    }}
                    variant="outlineSecondary"
                  >
                    <span className={saveButtonTextClassName}>
                      <Row className="items-center gap-1">
                        <SaveIcon className="h-3 w-3" />
                        Save & Close
                        {isLastJob && <KeyboardShortcut text="⌘↵" />}
                      </Row>
                    </span>
                    {loadingIndicator}
                  </Button>
                  <Button
                    className={MODAL_BUTTON_CLASS_NAME}
                    disabled={isLastJob || isCreateJobUpdatePending}
                    onClick={() => {
                      mixpanelUtils.trackButtonClick(
                        "Save & Next",
                        MIXPANEL_SCREENS.JOB_TRACKER,
                      );
                      void form.handleSubmit(handleSaveAndNext)();
                    }}
                    variant="outlineSecondary"
                  >
                    <span className={saveButtonTextClassName}>
                      <Row className="items-center gap-1">
                        <SaveIcon className="h-3 w-3" />
                        Save & Next
                        {!isLastJob && <KeyboardShortcut text="⌘↵" />}
                      </Row>
                    </span>
                    {loadingIndicator}
                  </Button>
                </Row>
              </Row>
              {errorMessage}
            </Column>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
